import { type VariantProps, cva } from 'class-variance-authority';
import type { ReactNode, JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import type { CommonProps } from '../../types';

export interface HeadingProps
    extends CommonProps,
        VariantProps<typeof HeadingStyles> {
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    children?: ReactNode;
    id?: string;
}

export const HeadingStyles = cva('font-bold', {
    variants: {
        size: {
            display:
                'text-3xl leading-10 xs:text-5xl xs:leading-[3.5rem] md:text-6xl md:leading-[4.5rem] mb-8',
            h1: 'text-3xl leading-10 xl:ml-20 xs:text-4xl xs:leading-[3rem] md:text-5xl md:leading-[3.5rem] mb-6',
            h2: 'text-2xl xs:text-3xl xs:leading-10 md:text-4xl md:leading-[3rem] mb-6',
            h3: 'text-xl leading-6 xs:text-2xl md:text-3xl md:leading-10 mb-6',
            h4: 'text-lg leading-6 xs:text-xl xs:leading-6 md:text-2xl mb-4',
            h5: 'text-base xs:text-lg xs:leading-6 md:text-xl md:leading-6 mb-4',
            h6: 'text-base mb-4',
        },
    },
    defaultVariants: {
        size: 'h2',
    },
});

export const Heading = ({
    size,
    as = 'h2',
    className,
    testId = 'heading',
    children,
    id,
    ...props
}: HeadingProps): JSX.Element => {
    const HeadingTag = as;

    if (!size) {
        size = HeadingTag;
    }

    return (
        <HeadingTag
            className={twMerge(HeadingStyles({ size, className }))}
            data-testid={testId}
            id={id}
            {...props}
        >
            {children}
        </HeadingTag>
    );
};
